/**
 * Tenant-aware currency formatter for the Admin app.
 * Mirror of saheeh-pos/src/hooks/useCurrency.ts.
 */

import { useEffect, useState } from 'react';
import { supabase } from '../services/supabaseClient';

export interface CurrencyState {
  code: string;
  symbol: string;
  region: string | null;
  fmt: (v: number | null | undefined) => string;
}

let cached: CurrencyState | null = null;
const listeners = new Set<(s: CurrencyState) => void>();

function build(code: string, region: string | null): CurrencyState {
  const symbol = code === 'SAR' ? 'SAR'
    : code === 'EUR' ? '€'
    : code === 'USD' ? '$'
    : code === 'AED' ? 'AED'
    : code;
  const fmt = (v: number | null | undefined): string => {
    const n = Number(v) || 0;
    if (code === 'SAR' || code === 'AED') return `${n.toFixed(2)} ${symbol}`;
    if (code === 'EUR' || code === 'USD') return `${symbol}${n.toFixed(2)}`;
    return `${n.toFixed(2)} ${symbol}`;
  };
  return { code, symbol, region, fmt };
}

const DEFAULT: CurrencyState = build('SAR', 'SA');

async function loadCurrency(): Promise<CurrencyState> {
  if (cached) return cached;
  try {
    const { data: session } = await supabase.auth.getSession();
    const tenantId = (session.session?.user?.app_metadata as any)?.tenant_id as string | undefined;
    if (!tenantId) { cached = DEFAULT; return cached; }
    const { data } = await supabase.rpc('public_tenant_info', { p_tenant_id: tenantId });
    const t: any = Array.isArray(data) ? data[0] : data;
    cached = build(t?.currency || 'SAR', t?.region || null);
    listeners.forEach((cb) => cb(cached!));
    return cached;
  } catch (err) {
    console.error('[useCurrency] load failed:', err);
    cached = DEFAULT;
    return cached;
  }
}

void loadCurrency();

export function useCurrency(): CurrencyState {
  const [state, setState] = useState<CurrencyState>(cached || DEFAULT);
  useEffect(() => {
    if (cached) { setState(cached); return; }
    let alive = true;
    const cb = (s: CurrencyState) => { if (alive) setState(s); };
    listeners.add(cb);
    void loadCurrency().then((s) => alive && setState(s));
    return () => { alive = false; listeners.delete(cb); };
  }, []);
  return state;
}

export function formatMoney(value: number | null | undefined): string {
  return (cached || DEFAULT).fmt(value);
}

export function currentCurrency(): string {
  return (cached || DEFAULT).code;
}
